<template>
    <evp-layout>
        <div v-if="evp.user" class="lg:hidden mb-6 bg-surface shadow-small rounded-md">
            <div class="flex flex-row items-center justify-between mx-6">
                <div class="flex flex-row justify-start items-center py-4">
                    <div class="w-14 shrink-0 mr-3">
                        <div
                            :class="[
                                'h-14 w-14 rounded-full border-[3px] overflow-hidden',
                                evp.user.isVerified ? 'border-[#36b37e]' : '',
                            ]"
                        >
                            <a :href="route('profile')">
                                <img src="/images/chrome/default-avatar.jpg" :alt="$t('global.ViewProfileButton')" />
                            </a>
                        </div>
                    </div>

                    <h1>
                        <a class="link-primary text-xl no-underline" :href="route('profile')">{{ evp.user.firstName }} {{ evp.user.lastName }}</a>
                    </h1>
                </div>
                <div>
                    <a :href="route('logout')" aria-label="$t('global.Logout')" class="button-transparent h-11 w-11">
                        <svg
                            version="1.1"
                            class="h-6 fill-current"
                            xmlns="http://www.w3.org/2000/svg"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            x="0px"
                            y="0px"
                            viewBox="0 0 24 24"
                            style="enable-background:new 0 0 24 24;"
                            xml:space="preserve"
                        ><path d="M14.6,15.6L16,17l5-5l-5-5l-1.4,1.4l2.6,2.6H7.5v2h9.7L14.6,15.6z M5,21h14c1.1,0,2-0.9,2-2v-2h-2v2H5V5h14v2h2V5 c0-1.1-0.9-2-2-2H5C3.9,3,3,3.9,3,5v14C3,20.1,3.9,21,5,21z" /></svg>
                    </a>
                </div>
            </div>
        </div>
        <div v-else class="mx-2 sm:mx-6 flex items-stretch lg:hidden mb-1.5">
            <a :href="route('login')" class="button-secondary w-full h-14 justify-start relative">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="fill-current h-8 ml-5 mr-4"
                    viewBox="0 0 24 24"
                ><path d="M0 0h24v24H0z" fill="none" /><path d="M10.09 15.59L11.5 17l5-5-5-5-1.41 1.41L12.67 11H3v2h9.67l-2.58 2.59zM19 3H5c-1.11 0-2 .9-2 2v4h2V5h14v14H5v-4H3v4c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" /></svg>
                <span>{{ $t('global.Login') }}</span>
            </a>
        </div>

        <div class="hidden lg:flex flex-row items-stretch h-[235px] ml-0.5 mb-0.5">
            <div class="w-[320px] h-full bg-cover bg-right bg-[url(@/images/title-banner/title-left.png)]"></div>
            <div class="grow h-full items-center justify-center hidden lg:flex bg-[length:7px_235px] bg-[url(@/images/title-banner/title-center-tile.png)]">
                <h1 class="text-center text-gray-500 text-xl lg:text-2xl whitespace-nowrap mt-0 mb-14">
                    <div v-html="$t('global.Tagline1')"></div>
                    <div v-html="$t('global.Tagline2')"></div>
                </h1>
            </div>
            <div class="w-[320px] h-full bg-cover bg-[url(@/images/title-banner/title-right.png)]"></div>
        </div>

        <div class="flex flex-row mb-10 mx-2 sm:mx-6 lg:mx-0 items-stretch">
            <nav class="w-full lg:w-[524px] shrink-0 flex flex-row flex-wrap mx-auto">
                <a :href="evpHomePage.isDesktopBrowser ? route('help-article', ['phone-call-cost']) : 'tel:8998'" class="button-primary w-full min-h-14 py-1 pr-2 lg:hidden justify-start lg:justify-center mb-1.5 lg:m-0.5 relative mb-1.5">
                    <div class="flex items-center justify-center h-12 w-[52px] mx-2.5 sm:mx-3">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-8 overflow-visible" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none" /><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                    </div>
                    <div>
                        <p>{{ $t('global.Call8998') }}</p>
                        <p class="text-xs">{{ $t('global.CostsApply') }}</p>
                    </div>
                </a>

                <a v-if="evpHomePage.showPatientHasArrivedButton" :href="route('patient-has-arrived')" class="button-primary w-full h-14 lg:hidden justify-start lg:justify-center lg:m-0.5 relative mb-6">
                    <svg
                        class="h-[28px] ml-[22px] mr-[21px]"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        x="0px"
                        y="0px"
                        viewBox="0 0 122.88 117.55"
                        style="enable-background:new 0 0 122.88 117.55"
                        xml:space="preserve"
                    ><g><path style="fill-rule:evenodd;clip-rule:evenodd;" d="M78.81,82.78c-4.35,4.77-9.42,9.05-15.12,12.51c-0.7,0.51-1.65,0.58-2.43,0.08 c-8.41-5.35-15.48-11.78-21.03-18.76c-7.66-9.61-12.49-20.27-14.14-30.53c-1.68-10.41-0.11-20.42,5.07-28.56 c2.04-3.22,4.65-6.15,7.83-8.68C46.3,3.01,54.65-0.06,62.96,0c8.01,0.06,15.91,3.05,22.74,9.28c2.4,2.18,4.42,4.68,6.07,7.39 c5.57,9.17,6.77,20.87,4.32,32.73c-2.41,11.71-8.41,23.62-17.28,33.35V82.78L78.81,82.78L78.81,82.78z M25.32,74.54 c1.98,0,3.59,1.61,3.59,3.59c0,1.98-1.61,3.59-3.59,3.59h-6.74l-8.88,28.67h103.22l-9.64-28.67h-5.57c-1.98,0-3.59-1.61-3.59-3.59 c0-1.98,1.61-3.59,3.59-3.59h10.7l14.46,43.01H0l13.32-43.01H25.32L25.32,74.54z M61.38,18.51c9.88,0,17.88,8.01,17.88,17.87 c0,9.88-8.01,17.88-17.88,17.88c-9.88,0-17.87-8-17.87-17.88C43.49,26.51,51.5,18.51,61.38,18.51L61.38,18.51L61.38,18.51z" /></g></svg>
                    <span>{{ $t('booking.IHaveArrived') }}</span>
                </a>

                <template v-if="evpHomePage.userCanAccessDoctorWorkplace || evpHomePage.verificationRequiredForDoctorWorkplaceAccess">
                    <a :href="route('doctor-workplace')" class="button-primary w-full h-14 lg:hidden lg:rounded-none justify-start mb-1.5 relative">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-[34px] mx-[19px]" viewBox="0 0 24 24"><path fill-rule="evenodd" clip-rule="evenodd" d="M14 6V4h-4v2h4zM4 8v11h16V8H4zm16-2c1.11 0 2 .89 2 2v11c0 1.11-.89 2-2 2H4c-1.11 0-2-.89-2-2l.01-11c0-1.11.88-2 1.99-2h4V4c0-1.11.89-2 2-2h4c1.11 0 2 .89 2 2v2h4z" /><path fill="none" d="M0 0h24v24H0z" /></svg>
                        <span>{{ $t('global.DoctorWorkplaceTitle') }}</span>
                    </a>
                </template>
                <a :href="route('booking')" class="button-primary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-12 lg:h-[90px] lg:absolute lg:left-1/2 lg:top-[45%] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-3 lg:mx-0"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        version="1.1"
                        baseProfile="full"
                        viewBox="0 0 75 75"
                        xml:space="preserve"
                    ><path stroke-width="0.2" stroke-linejoin="round" d="M 18.0025,57.0081L 18.0025,23.0032L 23.0032,23.0032L 23.0032,20.0028C 23.0033,18.898 23.8988,18.0025 25.0035,18.0025L 29.004,18.0025C 30.1087,18.0025 31.0042,18.898 31.0043,20.0026L 31.0043,23.0032L 45.0063,23.0032L 45.0062,20.0026C 45.0062,18.8978 45.9018,18.0023 47.0065,18.0023L 51.0071,18.0023C 52.1118,18.0023 53.0074,18.8978 53.0074,20.0026L 53.0074,23.0032L 58.0081,23.0032L 58.0081,57.0081L 18.0025,57.0081 Z M 21.0029,54.0077L 55.0076,54.0077L 55.0076,31.0044L 21.0029,31.0044L 21.0029,54.0077 Z M 23.0032,47.0066L 29.004,47.0066L 29.004,52.0073L 23.0032,52.0073L 23.0032,47.0066 Z M 31.0043,47.0066L 37.0051,47.0066L 37.0051,52.0073L 31.0043,52.0073L 31.0043,47.0066 Z M 39.0054,47.0066L 45.0063,47.0066L 45.0063,52.0073L 39.0054,52.0073L 39.0054,47.0066 Z M 47.0065,47.0066L 53.0074,47.0066L 53.0074,52.0073L 47.0065,52.0073L 47.0065,47.0066 Z M 23.0032,40.0056L 29.004,40.0056L 29.004,45.0063L 23.0032,45.0063L 23.0032,40.0056 Z M 31.0043,40.0056L 37.0051,40.0056L 37.0051,45.0063L 31.0043,45.0063L 31.0043,40.0056 Z M 39.0054,40.0056L 45.0063,40.0056L 45.0063,45.0063L 39.0054,45.0063L 39.0054,40.0056 Z M 47.0065,40.0056L 53.0074,40.0056L 53.0074,45.0063L 47.0065,45.0063L 47.0065,40.0056 Z M 31.0043,33.0048L 37.0051,33.0048L 37.0051,38.0055L 31.0043,38.0055L 31.0043,33.0048 Z M 39.0054,33.0048L 45.0063,33.0048L 45.0063,38.0055L 39.0054,38.0055L 39.0054,33.0048 Z M 47.0065,33.0048L 53.0074,33.0048L 53.0074,38.0055L 47.0065,38.0055L 47.0065,33.0048 Z M 48.5067,20.0028C 47.6782,20.0028 47.0065,20.6745 47.0065,21.5031L 47.0065,24.5035C 47.0065,25.332 47.6782,26.0037 48.5067,26.0037L 49.5069,26.0037C 50.3354,26.0037 51.0071,25.332 51.0071,24.5035L 51.0071,21.5031C 51.0071,20.6745 50.3354,20.0028 49.5069,20.0028L 48.5067,20.0028 Z M 26.5037,20.0028C 25.6751,20.0028 25.0035,20.6745 25.0035,21.503L 25.0035,24.5034C 25.0035,25.332 25.6751,26.0037 26.5037,26.0037L 27.5038,26.0037C 28.3324,26.0037 29.004,25.332 29.004,24.5034L 29.004,21.503C 29.004,20.6745 28.3324,20.0028 27.5038,20.0028L 26.5037,20.0028 Z "></path></svg>
                    <span>{{ $t('global.AppointmentTitle') }}</span>
                </a>
                <a :href="route('booking-telemedicine')" class="button-primary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative">
                    <VideoConsultationIcon class="h-8 lg:h-[76px] lg:mt-0.5 lg:absolute lg:left-1/2 lg:top-[68px] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-[20px] lg:mx-0" />
                    <span>{{ $t('global.TelemedicineTitle') }}</span>
                </a>
                <a :href="route('bills')" class="button-primary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-12 lg:h-[90px] lg:absolute lg:left-1/2 lg:top-[45%] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-3 lg:mx-0"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        version="1.1"
                        baseProfile="full"
                        viewBox="0 0 75.00 75.00"
                        xml:space="preserve"
                    ><path d="M 51.4237,48.8848C 50.7931,49.2215 49.8882,49.5476 48.7089,49.8629C 47.5297,50.1782 46.2685,50.3472 44.9253,50.3698C 43.486,50.3769 42.1321,50.1485 40.8638,49.6844C 39.5955,49.2203 38.4982,48.4778 37.5719,47.4568C 36.7168,46.4954 36.0898,45.1626 35.6908,43.4585L 49.9986,43.4585L 49.9986,39.5398L 34.8927,39.5398C 34.8927,39.2394 34.8927,38.9079 34.8927,38.5453C 34.8832,37.8721 34.9022,37.2298 34.9497,36.6185L 49.9986,36.6185L 49.9986,32.6998L 35.8048,32.6998C 36.1848,31.1509 36.7928,29.8925 37.6289,28.9245C 38.5243,27.8536 39.5694,27.0623 40.7641,26.5506C 41.9588,26.0389 43.2318,25.7849 44.5833,25.7885C 45.854,25.8067 47.0463,25.9591 48.1603,26.2457C 49.2742,26.5323 50.21,26.8444 50.9677,27.182L 52.5638,20.7122C 51.5235,20.2469 50.2694,19.852 48.8016,19.5276C 47.3337,19.2032 45.7376,19.0339 44.0133,19.0198C 41.2942,19.0327 38.8275,19.5428 36.6132,20.5499C 34.3988,21.557 32.505,22.9836 30.9319,24.8297C 29.0913,26.9158 27.8084,29.5392 27.0833,32.6998L 23.2788,32.6998L 23.2788,36.6185L 26.4697,36.6185C 26.4511,37.2401 26.4326,37.8617 26.414,38.4832C 26.414,38.8665 26.414,39.2187 26.414,39.5398L 23.2788,39.5398L 23.2788,43.4585L 26.9717,43.4585C 27.5109,46.4794 28.5521,48.9483 30.0953,50.8652C 31.6842,52.9082 33.6699,54.4627 36.0523,55.5289C 38.4347,56.5951 41.0504,57.1317 43.8993,57.1385C 45.7448,57.1168 47.4406,56.8989 48.9868,56.4851C 50.533,56.0712 51.8014,55.592 52.7918,55.0474L 51.4237,48.8848 Z "></path></svg>
                    <span>{{ $t('global.PaymentTitle') }}</span>
                </a>
                <a :href="route('documents')" class="button-primary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-12 lg:h-[90px] lg:absolute lg:left-1/2 lg:top-[45%] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-3 lg:mx-0"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        version="1.1"
                        baseProfile="full"
                        viewBox="0 0 75.00 75.00"
                        xml:space="preserve"
                    ><path d="M 23,19L 53,19L 56.9999,25.0001L 57,27L 19,27L 19,25.0001L 23,19 Z M 19,42L 19,29.0001L 56.9999,29.0001L 57,42L 54.038,42L 52.7168,35.7843L 23.4742,42L 19,42 Z M 34,33L 34,35L 42,35L 42,33L 34,33 Z M 19,57L 19,44L 56.9999,44L 57,57L 19,57 Z M 34,48L 34,50L 42,50L 42,48L 34,48 Z "></path></svg>
                    <span>{{ $t('global.DocumentTitle') }}</span>
                </a>
                <a :href="route('sos')" class="button-primary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative">
                    <svg
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-12 lg:h-[90px] lg:absolute lg:left-1/2 lg:top-[45%] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-3 lg:mx-0"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        x="0px"
                        y="0px"
                        viewBox="0 0 75 75"
                        style="enable-background:new 0 0 75 75;"
                        xml:space="preserve"
                    ><path d="M57.9,38.1L53.4,33l0.6-6.7l-6.6-1.5L44,18.9l-6.2,2.7l-6.2-2.7l-3.5,5.8l-6.6,1.5l0.6,6.7l-4.5,5.1l4.5,5.1l-0.6,6.7l6.6,1.5l3.5,5.8l6.2-2.7l6.2,2.7l3.5-5.8l6.6-1.5l-0.6-6.7L57.9,38.1z M39.6,47.2H36v-3.7h3.7V47.2z M39.6,39.9H36v-11h3.7V39.9z" /></svg>
                    <span>{{ $t('global.SosTitle') }}</span>
                </a>
                <a :href="route('favorite-specialists')" class="button-primary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative">
                    <div class="flex items-center justify-center h-12 w-12 lg:h-[90px] lg:w-[90px] lg:absolute lg:left-1/2 lg:top-[45%] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-3 lg:mx-0">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-6 lg:h-[56px] overflow-visible"
                            version="1.1"
                            xmlns:xlink="http://www.w3.org/1999/xlink"
                            x="0px"
                            y="0px"
                            viewBox="0 0 122.88 109.57"
                            style="enable-background:new 0 0 122.88 109.57"
                            xml:space="preserve"
                        ><g><path d="M65.46,19.57c-0.68,0.72-1.36,1.45-2.2,2.32l-2.31,2.41l-2.4-2.33c-0.71-0.69-1.43-1.4-2.13-2.09 c-7.42-7.3-13.01-12.8-24.52-12.95c-0.45-0.01-0.93,0-1.43,0.02c-6.44,0.23-12.38,2.6-16.72,6.65c-4.28,4-7.01,9.67-7.1,16.57 c-0.01,0.43,0,0.88,0.02,1.37c0.69,19.27,19.13,36.08,34.42,50.01c2.95,2.69,5.78,5.27,8.49,7.88l11.26,10.85l14.15-14.04 c2.28-2.26,4.86-4.73,7.62-7.37c4.69-4.5,9.91-9.49,14.77-14.52c3.49-3.61,6.8-7.24,9.61-10.73c2.76-3.42,5.02-6.67,6.47-9.57 c2.38-4.76,3.13-9.52,2.62-13.97c-0.5-4.39-2.23-8.49-4.82-11.99c-2.63-3.55-6.13-6.49-10.14-8.5C96.5,7.29,91.21,6.2,85.8,6.82 C76.47,7.9,71.5,13.17,65.46,19.57L65.46,19.57z M60.77,14.85C67.67,7.54,73.4,1.55,85.04,0.22c6.72-0.77,13.3,0.57,19.03,3.45 c4.95,2.48,9.27,6.1,12.51,10.47c3.27,4.42,5.46,9.61,6.1,15.19c0.65,5.66-0.29,11.69-3.3,17.69c-1.7,3.39-4.22,7.03-7.23,10.76 c-2.95,3.66-6.39,7.44-10,11.17C97.2,74.08,91.94,79.12,87.2,83.66c-2.77,2.65-5.36,5.13-7.54,7.29L63.2,107.28l-2.31,2.29 l-2.34-2.25l-13.6-13.1c-2.49-2.39-5.37-5.02-8.36-7.75C20.38,71.68,0.81,53.85,0.02,31.77C0,31.23,0,30.67,0,30.09 c0.12-8.86,3.66-16.18,9.21-21.36c5.5-5.13,12.97-8.13,21.01-8.42c0.55-0.02,1.13-0.03,1.74-0.02C46,0.48,52.42,6.63,60.77,14.85 L60.77,14.85z" /></g></svg>
                    </div>
                    <span>{{ $t('global.MyDoctorsTitle') }}</span>
                </a>
                <a :href="route('article')" class="button-primary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-12 lg:h-[90px] lg:absolute lg:left-1/2 lg:top-[45%] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-3 lg:mx-0" viewBox="0 0 75 75"><path d="M 27,48C 27,50 26.5,53 25.5,53L 52,53C 54.5,53 55,50.5 55,48L 55,25L 27,25L 27,48 Z M 59,48C 59,52.3723 56,57 52.5,57L 21,57C 18.3766,57 16,52.8723 16,48.5L 16,27L 23,27L 23,21L 59,21L 59,48 Z M 20,31L 20,49C 20,51.6234 20.6255,53.25 21.5,53.25C 22.3744,53.25 23,51.6234 23,49L 23,31L 20,31 Z M 41,34L 53,34L 53,48L 41,48L 41,34 Z M 29,28L 53,28L 53,31L 29,31L 29,28 Z M 29,34L 39,34L 39,36L 29,36L 29,34 Z M 29,38L 39,38L 39,40L 29,40L 29,38 Z M 29,42L 39,42L 39,44L 29,44L 29,42 Z M 29,46L 39,46L 39,48L 29,48L 29,46 Z"></path></svg>
                    <span>{{ $t('global.ArticleTitle') }}</span>
                </a>
                <a :href="route('app')" class="button-secondary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-12 lg:h-[90px] lg:absolute lg:left-1/2 lg:top-[45%] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-3 lg:mx-0"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        version="1.1"
                        baseProfile="full"
                        viewBox="0 0 76.00 76.00"
                        xml:space="preserve"
                    ><path
                        d=" M46.8,19H29.2c-2.2,0-3.9,1.7-3.9,3.9v30.2c0,2.2,1.7,3.9,3.9,3.9h17.5c2.2,0,3.9-1.7,3.9-3.9V22.9
        C50.7,20.7,48.9,19,46.8,19z M46.8,54H29.2c-0.5,0-0.9-0.4-0.9-0.9V22.9c0-0.5,0.4-0.9,0.9-0.9h3.2v0.9c0.1,0.5,0.5,0.9,0.9,0.9
        h9.3c0.5,0,0.9-0.4,0.9-0.9V22h3.2c0.5,0,0.9,0.4,0.9,0.9v30.2h0.1C47.7,53.6,47.3,54,46.8,54z "
                    ></path></svg>
                    <span>{{ $t('global.AppTitle') }}</span>
                </a>
                <a :href="route('help')" class="button-secondary w-full h-14 lg:rounded-none lg:h-[170px] lg:w-[170px] lg:pt-[96px] justify-start lg:justify-center mb-1.5 lg:m-0.5 relative" rel="help">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-12 lg:h-[90px] lg:absolute lg:left-1/2 lg:top-[45%] lg:translate-x-[-50%] lg:translate-y-[-50%] mx-3 lg:mx-0"
                        xmlns:xlink="http://www.w3.org/1999/xlink"
                        version="1.1"
                        baseProfile="full"
                        viewBox="0 0 76.01 76.01"
                        enable-background="new 0 0 76.01 76.01"
                        xml:space="preserve"
                    ><path stroke-width="0.2" stroke-linejoin="round" d="M 37.2083,49.0833C 39.3945,49.0833 41.1667,50.8555 41.1667,53.0417C 41.1667,55.2278 39.3945,57 37.2083,57C 35.0222,57 33.25,55.2278 33.25,53.0417C 33.25,50.8555 35.0222,49.0833 37.2083,49.0833 Z M 38,17.4167C 44.9956,17.4167 50.6666,21.9416 50.6666,28.5C 50.6666,30.875 49.0833,34.8333 45.9167,36.4167C 42.75,38 41.1667,40.1267 41.1667,42.75L 41.1667,45.9167L 33.25,45.9167L 33.25,43.5417C 33.25,38.1571 38,34.8333 39.5833,33.25C 42.75,30.0833 42.75,29.644 42.75,28.5C 42.75,25.8767 40.6233,23.75 38,23.75C 35.3766,23.75 33.25,25.8767 33.25,28.5L 33.25,30.875L 25.3333,30.875L 25.3333,29.2917C 25.3333,22.7333 31.0044,17.4167 38,17.4167 Z"></path></svg>
                    <span>{{ $t('global.HelpTitle') }}</span>
                </a>
            </nav>

            <div class="grow m-0.5 hidden lg:flex flex-col">
                <template v-if="evp.user">

                    <div class="bg-surface flex flex-col h-full">
                        <a :href="route('logout')" aria-label="$t('global.Logout')" class="relative z-10 button-transparent-light self-end flex space-x-2 py-2 px-4 justify-center items-center rounded-none -mb-px -mr-px">
                            <svg
                                version="1.1"
                                class="h-7 fill-current"
                                xmlns="http://www.w3.org/2000/svg"
                                xmlns:xlink="http://www.w3.org/1999/xlink"
                                x="0px"
                                y="0px"
                                viewBox="0 0 24 24"
                                style="enable-background:new 0 0 24 24;"
                                xml:space="preserve"
                            ><path d="M14.6,15.6L16,17l5-5l-5-5l-1.4,1.4l2.6,2.6H7.5v2h9.7L14.6,15.6z M5,21h14c1.1,0,2-0.9,2-2v-2h-2v2H5V5h14v2h2V5 c0-1.1-0.9-2-2-2H5C3.9,3,3,3.9,3,5v14C3,20.1,3.9,21,5,21z" /></svg>
                            <span>{{ $t('global.Logout') }}</span>
                        </a>
                        <hr />

                        <div class="flex flex-col items-center grow mt-[70px] px-4">
                            <div :class="['h-16 w-16 rounded-full border-[3px] overflow-hidden', evp.user.isVerified ? 'border-[#36b37e]' : '']">
                                <a :href="route('profile')">
                                    <img src="/images/chrome/default-avatar.jpg" :alt="$t('global.ViewProfileButton')" />
                                </a>
                            </div>

                            <h2 class="mt-3">
                                <a class="text-primary-dark text-2xl no-underline border-b-2 border-transparent hover:border-primary-dark pb-px" :href="route('profile')">
                                    {{ evp.user.firstName }} {{ evp.user.lastName }}
                                </a>
                            </h2>

                            <template v-if="evpHomePage.userCanAccessDoctorWorkplace || evpHomePage.verificationRequiredForDoctorWorkplaceAccess">
                                <div class="mt-4 text-secondary text-center">
                                    <div v-for="(name, index) in evpHomePage.userInstitutionNames.slice(0, 3)" :key="index">
                                        {{ name }}
                                    </div>

                                    <div v-if="evpHomePage.userInstitutionNames.length > 3">
                                        {{ $tChoice('global.AndMoreInstitutions', evpHomePage.userInstitutionNames.length - 3) }}
                                    </div>
                                </div>

                                <a :href="route('doctor-workplace')" class="button-primary h-12 px-5 justify-center mt-10 py-1.5 space-x-4">
                                    <span>{{ $t('global.OpenDoctorWorkplace') }}</span>
                                    <svg class="h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 297 297"><polygon points="215.709,49.188 165.041,49.188 224.686,123.188 0,123.188 0,173.188 226.005,173.188 165.041,247.188 215.709,247.188 296.375,149.27 " /></svg>
                                </a>
                            </template>
                            <template v-else>
                                <a :href="route('booking-institutions')" class="button-primary h-12 px-5 justify-center mt-12 py-1.5 space-x-4">
                                    <span>{{ $t('booking.MakeNewAppointment') }}</span>
                                    <svg class="h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 297 297"><polygon points="215.709,49.188 165.041,49.188 224.686,123.188 0,123.188 0,173.188 226.005,173.188 165.041,247.188 215.709,247.188 296.375,149.27 " /></svg>
                                </a>
                            </template>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="bg-surface shadow-small px-5 flex flex-col items-center justify-center h-full">
                        <login-form />
                    </div>
                </template>
            </div>
        </div>

        <template v-if="showAds">
            <!-- <ārsta darbavietas reklāma> -->
            <div v-if="bannerLoading" class="animate-pulse-in-out h-[390px] border-2 border-dashed border-gray-400 mb-7"></div>

            <ad-banner
                :advert="doctorWorkplaceAdvert"
                class="mb-7"
            />
            <!-- </ārsta darbavietas reklāma> -->

            <template v-if="advert">
                <div v-if="bannerLoading" class="animate-pulse-in-out h-[132px] border-2 border-dashed border-gray-400 mb-7"></div>

                <ad-banner
                    v-else
                    :advert="advert"
                    class="mb-7"
                />
            </template>
        </template>

        <div v-if="recentArticles.length || articlesLoading" class="px-2 lg:px-0.5">
            <div class="pl-11 text-lg text-gray-800 my-2">
                {{ $t('global.NewArticles') }}
            </div>

            <div class="flex flex-wrap justify-between items-center">

                <template v-if="articlesLoading">
                    <div
                        v-for="n in 4"
                        :key="n"
                        class="animate-pulse-in-out flex w-full lg:w-[312px] h-[132px] mb-1 border-2 border-dashed border-gray-400"
                    ></div>
                </template>

                <template v-else>
                    <article-box
                        v-for="(article, index) in recentArticles"
                        :key="index"
                        :article-data="article"
                        class="w-full lg:w-[312px] h-[132px] mb-1"
                    />
                </template>

            </div>
        </div>
    </evp-layout>
</template>

<script setup>
    import { ref } from 'vue'
    import { route } from 'ziggy'
    import Http from '@/js/global/http'
    import EvpLayout from '@/components/EvpLayout.vue'
    import AdBanner from '@/components/AdBanner.vue'
    import ArticleBox from '@/components/articles/ArticleBox.vue'
    import LoginForm from '@/components/home/LoginForm.vue'
    import VideoConsultationIcon from '@/components/VideoConsultationIcon.vue'

    const evp = window.evp
    const evpHomePage = window.evpHomePage
    const advert = window.advert.data
    const showAds = window.advert.show

    const bannerLoading = ref(false)
    const articlesLoading = ref(true)

    const recentArticles = ref([])


    Http.get(route('recent-articles'), {
        success(response) {
            if (response.articles) {
                recentArticles.value = response.articles
            }
        },
        always() {
            articlesLoading.value = false
        }
    })


    const doctorWorkplaceAdvert = {
        LargeBannerUrl: 'https://bildes.eveselibaspunkts.lv/e5f1199c-ab43-4561-a439-ceb1b497e537.jpg',
        SmallBannerUrl: 'https://bildes.eveselibaspunkts.lv/c94900ca-c732-47a2-9036-a744e71b6d71.jpg',
        ImageAlt: 'EVP ārsta darbavietas kalendārs',
        RedirectUrl: 'https://eveselibaspunkts.lv/kalendars',
    }


    if (showAds && advert) {
        bannerLoading.value = true

        preloadBannerImages().finally(() => {
            bannerLoading.value = false
        })
    }

    async function preloadBannerImages() {
        const images = [
            advert.LargeBannerUrl,
            advert.SmallBannerUrl,
            doctorWorkplaceAdvert.LargeBannerUrl,
            doctorWorkplaceAdvert.SmallBannerUrl,
        ]

        await Promise.all(images.map(url => new Promise((resolve, reject) => {
            const image = new Image()

            image.onload = resolve
            image.onerror = reject
            image.src = url
        })))
    }

</script>